'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type AuthContextType = {
    jwt: string | null;
    userId: number | null;
    userName: string | null;
    login: (token: string, userId: number, userName: string) => void;
    logout: () => void;
    isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_EXPIRATION_MS = 24 * 60 * 60 * 1000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [jwt, setJwt] = useState<string | null>(null);
    const [userId, setUserId] = useState<number | null>(null);
    const [userName, setUserName] = useState<string | null>(null);

    useEffect(() => {
        const storedToken = localStorage.getItem('jwt');
        const storedUserId = localStorage.getItem('userId');
        const storedUserName = localStorage.getItem('userName');
        const storedTimestamp = localStorage.getItem('jwt_timestamp');

        if (storedToken && storedUserId && storedTimestamp && storedUserName) {
            const now = Date.now();
            const tokenTime = Number(storedTimestamp);

            if (now - tokenTime < TOKEN_EXPIRATION_MS) {
                setJwt(storedToken);
                setUserId(Number(storedUserId));
                setUserName(storedUserName);
            } else {
                logout();
            }
        }
    }, []);

    const login = (token: string, userId: number, userName: string) => {
        const timestamp = Date.now();
        setJwt(token);
        setUserId(userId);
        setUserName(userName);
        localStorage.setItem('jwt', token);
        localStorage.setItem('userId', userId.toString());
        localStorage.setItem('userName', userName); // 👈
        localStorage.setItem('jwt_timestamp', timestamp.toString());
    };

    const logout = () => {
        setJwt(null);
        setUserId(null);
        setUserName(null);
        localStorage.removeItem('jwt');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        localStorage.removeItem('jwt_timestamp');
    };

    const value: AuthContextType = {
        jwt,
        userId,
        userName,
        login,
        logout,
        isAuthenticated: !!jwt,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
